import React, { useState } from 'react';
import { Skeleton } from 'components/shadcn/ui/skeleton';
import { useNavigate } from 'react-router-dom';
import HGStatus from 'components/HGStatus';
import { useGetIDsFromURL } from 'components/DeviceDetails/helper';
import { ProfileTypes, DeviceTypes } from 'components/DeviceDetails/constants';
import {
    verifySingleStack,
    getFaultsAndAlarms,
} from 'components/DeviceDetails/helper';
import { useProfileData } from './helper';
import { FIELDS_CONFIG } from 'components/DeviceDetails/constants';
import HGClickToCopy from 'components/HGClickToCopy';
import HGTooltip from '../../components/HGTooltip';
import EditNameButton from '../EditNameButton';
import HGVerified from 'components/HGVerified';
import InviteInstallerModal from 'components/Users/Installers/InviteInstallerModal';
import { useVerifyAdmin, useVerifyCompanyAdmin } from 'helper';
import InviteCustomerModal from 'components/Users/Customers/InviteCustomerModal';
import { Button } from 'components/shadcn/ui/button';
import ActiveAlerts from 'components/Alerts/ActiveAlerts';
import ChildAlertsSummary from 'components/Alerts/ChildAlertsSummary';

const HGProfile = () => {
    const navigate = useNavigate();
    const {
        data: profileData,
        loading,
        deviceType,
        bmsConfigData,
        moduleConfigData,
        deviceOnline,
    } = useProfileData();

    const { SINGLE_BMS_ID } = verifySingleStack(profileData);

    const profileType =
        deviceType === DeviceTypes.DEFAULT ? ProfileTypes.Sites : deviceType;

    const fieldsLabels =
        profileType === ProfileTypes.Sites
            ? FIELDS_CONFIG.site
            : FIELDS_CONFIG[deviceType] || {};

    const keyValuePairs = Object.entries(profileData ? fieldsLabels : {});
    const isAdmin = useVerifyAdmin();
    const isCompanyAdmin = useVerifyCompanyAdmin();

    const [openInviteCustomerModal, setOpenInviteCustomerModal] =
        useState(false);

    const { baseRoute, pathname, BMS_ID, Module_ID, Thing_ID } =
        useGetIDsFromURL();
    const serialNumberCopy = Module_ID ?? BMS_ID ?? profileData?.SerialNumber;

    const [openInviteModal, setOpenInviteModal] = useState(false);

    const getMasterSnForPack = (masterSns, packSn) => {
        const master = masterSns?.find((m) =>
            m.PACKS?.some((p) => p.PACK_SN === packSn)
        );
        return master?.MASTER_SN ?? null;
    };

    const buildAlerts = (
        thingId,
        serialNumber,
        activeAlerts,
        masterSns,
        expectedDeviceType = null
    ) =>
        getFaultsAndAlarms(serialNumber, activeAlerts, expectedDeviceType).map(
            (alert) => {
                const masterSerialNumber =
                    alert.deviceType === 'MASTER'
                        ? alert.alertSerialNumber
                        : getMasterSnForPack(
                              masterSns,
                              alert.alertSerialNumber
                          );
                const isSingleStack = (masterSns?.length ?? 0) < 2;
                return { ...alert, thingId, masterSerialNumber, isSingleStack };
            }
        );

    const collectChildAlerts = (masterSns, activeAlerts, thingId) => {
        const alerts = [];
        masterSns?.forEach((bms) => {
            alerts.push(
                ...buildAlerts(
                    thingId,
                    bms.MASTER_SN,
                    activeAlerts,
                    masterSns,
                    'MASTER'
                )
            );
            bms.PACKS?.forEach((module) => {
                alerts.push(
                    ...buildAlerts(
                        thingId,
                        module.PACK_SN,
                        activeAlerts,
                        masterSns,
                        'PACK'
                    )
                );
            });
        });
        const seen = new Set();
        return alerts.filter((a) => {
            const key = `${a.alertSerialNumber}-${a.level}-${a.message}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    };

    const ownDeviceType =
        profileType === ProfileTypes.EMS
            ? 'EMS'
            : profileType === ProfileTypes.BMS
              ? 'MASTER'
              : profileType === ProfileTypes.MODULE
                ? 'PACK'
                : null;

    const alertMessages =
        profileType === 'sites'
            ? profileData?.Things?.items?.reduce((acc, item) => {
                  const masterSns =
                      typeof item.Config === 'string'
                          ? JSON.parse(item.Config)?.MASTER_SNS
                          : item.Config?.data?.MASTER_SNS;
                  return acc.concat(
                      buildAlerts(
                          item.Thing_ID,
                          item.SerialNumber,
                          item.active_alerts,
                          masterSns,
                          'EMS'
                      )
                  );
              }, [])
            : buildAlerts(
                  Thing_ID,
                  serialNumberCopy,
                  profileData?.active_alerts,
                  profileData?.Config?.data?.MASTER_SNS,
                  ownDeviceType
              );

    const childAlerts = (() => {
        if (profileType === 'sites') {
            return (
                profileData?.Things?.items?.reduce((acc, item) => {
                    const masterSns =
                        typeof item.Config === 'string'
                            ? JSON.parse(item.Config)?.MASTER_SNS
                            : item.Config?.data?.MASTER_SNS;
                    return acc.concat(
                        collectChildAlerts(
                            masterSns,
                            item.active_alerts,
                            item.Thing_ID
                        )
                    );
                }, []) ?? []
            );
        }
        if (profileType === ProfileTypes.EMS) {
            return collectChildAlerts(
                profileData?.Config?.data?.MASTER_SNS,
                profileData?.active_alerts,
                Thing_ID
            );
        }
        if (profileType === ProfileTypes.BMS) {
            return collectChildAlerts(
                bmsConfigData ? [bmsConfigData] : [],
                profileData?.active_alerts,
                Thing_ID
            ).filter((a) => a.deviceType !== 'MASTER');
        }
        return [];
    })();

    const deviceName = () => {
        let name;
        let item;

        if (profileType === ProfileTypes.MODULE) {
            name = moduleConfigData?.PackName;
            item = moduleConfigData;
        } else if (profileType === ProfileTypes.BMS && !SINGLE_BMS_ID) {
            // For multi-stack Systems
            name = bmsConfigData?.MasterName;
            item = bmsConfigData;
        } else {
            name = profileData?.ThingName;
        }

        return name ? (
            <div className="text-sm font-light">{name}</div>
        ) : (
            <EditNameButton item={item} />
        );
    };

    let isVerifiedCustomer = '';
    let verifiedCustomer = profileData?.CustomerInfo ? true : false;
    if (profileData?.CustomerEmail) {
        isVerifiedCustomer = (
            <div className="flex items-center">
                <HGVerified
                    verified={verifiedCustomer ? 'verified' : 'not verified'}
                    type="customer"
                />
                {!verifiedCustomer && (isAdmin || isCompanyAdmin) && (
                    <>
                        <Button
                            variant="outline"
                            size="sm"
                            className="ml-2 text-[9px]"
                            onClick={() =>
                                setOpenInviteCustomerModal(
                                    !openInviteCustomerModal
                                )
                            }
                        >
                            Invite
                        </Button>
                        <InviteCustomerModal
                            open={openInviteCustomerModal}
                            toggle={setOpenInviteCustomerModal}
                        />
                    </>
                )}
            </div>
        );
    }
    let installerBadge = '';
    let verifiedInstaller = profileData?.InstallerInfo ? true : false;
    if (profileData?.InstallerEmail) {
        installerBadge = (
            <div className="flex items-center">
                <HGVerified
                    verified={verifiedInstaller ? 'verified' : 'not verified'}
                    type="installer"
                />
                {!verifiedInstaller && (isAdmin || isCompanyAdmin) && (
                    <>
                        <Button
                            size="sm"
                            className="ml-2 text-[9px]"
                            onClick={() => setOpenInviteModal(!openInviteModal)}
                        >
                            Invite
                        </Button>
                        <InviteInstallerModal
                            open={openInviteModal}
                            toggle={setOpenInviteModal}
                        />
                    </>
                )}
            </div>
        );
    }

    let route;
    if (baseRoute === 'devices') {
        route = `${pathname}/edit-device-profile`;
    } else {
        route = `${pathname}/edit-site`;
    }

    return (
        <div className="w-full p-4">
            {!loading && keyValuePairs?.length > 0 && (
                <div className="mb-3 flex items-center justify-end gap-2">
                    {/** alerts go here */}
                    {profileData?.SiteLink && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/${profileData.SiteLink}`)}
                        >
                            View Site
                        </Button>
                    )}
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(route)}
                        className={`flex items-center align-middle`}
                    >
                        <div>Edit</div>
                    </Button>
                </div>
            )}

            {loading ? (
                <div
                    className="grid max-w-[1000px] gap-8"
                    style={{
                        gridTemplateColumns:
                            'repeat(auto-fill, minmax(250px, 1fr))',
                    }}
                >
                    {Array.from({ length: 8 }).map((_, idx) => (
                        <div key={idx} className="flex space-x-2">
                            <Skeleton className="h-5 w-24" />
                            <Skeleton className="h-5 w-full" />
                        </div>
                    ))}
                </div>
            ) : keyValuePairs?.length === 0 ? (
                <h2>No data available</h2>
            ) : (
                <>
                    <div
                        className="grid max-w-[1000px] gap-8"
                        style={{
                            gridTemplateColumns:
                                'repeat(auto-fill, minmax(250px, 1fr))',
                        }}
                    >
                        {keyValuePairs.map(([key, value]) => {
                            const badgeAlignment =
                                (key === 'CustomerEmail' && verifiedCustomer) ||
                                (key === 'Installer' && verifiedInstaller)
                                    ? 'flex-row'
                                    : 'flex-col';
                            return (
                                key &&
                                value && (
                                    <div
                                        key={key}
                                        className="flex min-w-[250px] items-center justify-start"
                                    >
                                        <div
                                            bold
                                            className="whitespace-nowrap text-sm font-bold"
                                        >
                                            {`${value}: `}
                                        </div>
                                        <div
                                            className={`ml-2 block items-center md:flex ${badgeAlignment}`}
                                        >
                                            {key === 'Status' ? (
                                                <HGStatus
                                                    online={deviceOnline}
                                                />
                                            ) : key === 'Microinverters' ? (
                                                profileData[key]?.length
                                            ) : key === 'SerialNumber' ||
                                              key === 'device_sn' ? (
                                                <HGClickToCopy
                                                    content={serialNumberCopy}
                                                >
                                                    <HGTooltip
                                                        title={serialNumberCopy}
                                                    />
                                                </HGClickToCopy>
                                            ) : profileType ===
                                                  ProfileTypes.MODULE &&
                                              (key === 'hw_ver' ||
                                                  key === 'sw_ver') ? (
                                                (moduleConfigData[key] ?? 'N/A')
                                            ) : key === 'ThingName' ? (
                                                deviceName()
                                            ) : (
                                                <div className="whitespace-nowrap text-sm font-light">
                                                    {profileData[key]?.length >
                                                    25 ? (
                                                        <HGTooltip
                                                            title={
                                                                profileData[key]
                                                            }
                                                        />
                                                    ) : (
                                                        (profileData[key] ??
                                                        'N/A')
                                                    )}
                                                </div>
                                            )}
                                            {/** Badges Next to Values */}
                                            {key === 'CustomerEmail'
                                                ? isVerifiedCustomer
                                                : ''}
                                            {key === 'Installer'
                                                ? installerBadge
                                                : ''}
                                        </div>
                                    </div>
                                )
                            );
                        })}
                    </div>
                </>
            )}
            {/* ACTIVE ALERTS */}
            {alertMessages && (
                <div className="mt-5">
                    <ActiveAlerts alertMessages={alertMessages} />
                    <ChildAlertsSummary childAlerts={childAlerts} />
                </div>
            )}
        </div>
    );
};

export default HGProfile;
